import type { CaregiverId, PatientId } from "../../shared/schema/schema";
import { reactBridge } from "../../core";
import type { EntityProfileCalendarVisit } from "../entityProfileModal/sections/calendar/entityProfileCalendar.utils";
import { getCaregiverFullName } from "./caregiverProfileModal.utils";
import { CaregiverProfileCaregiver } from "./caregiverProfileModal.types";

export type CaregiverProfileAction =
  | "exportProfile"
  | "openChat"
  | "openExportProfileSettings"
  | "openChangeStatus"
  | "openNotes"
  | "openNewCalendarNote"
  | "openNewAbsence"
  | "setCalendarPto"
  | "approveCalendarClockLocation"
  | "openExclusionLists"
  | "openLegacyCaregiverSection"
  | "openCaregiverPayRate"
  | "openCaregiverPayRateReset"
  | "openCaregiverHoursOverride"
  | "addDocumentUpload"
  | "viewDocumentUpload"
  | "downloadDocumentUpload"
  | "sendDocumentUploadToEmail"
  | "sendDocumentUploadToFax"
  | "removeDocumentUpload"
  | "unblockLogin";

function dispatchCaregiverProfileAction(params: {
  action: CaregiverProfileAction;
  calendarDate?: string;
  caregiver: CaregiverProfileCaregiver;
  onDone?: () => void;
  legacySectionLabel?: string;
  legacySubsectionLabel?: string;
  selectedVisits?: EntityProfileCalendarVisit[];
  data?: unknown;
}) {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: {
        type: "caregiverProfileAction",
        payload: {
          action: params.action,
          calendarDate: params.calendarDate,
          caregiverId: params.caregiver.id,
          caregiverName: getCaregiverFullName(params.caregiver),
          caregiver: params.caregiver,
          legacySectionLabel: params.legacySectionLabel,
          legacySubsectionLabel: params.legacySubsectionLabel,
          onDone: params.onDone,
          selectedVisits: params.selectedVisits,
          data: params.data,
        },
      },
    })
  );
}

type CaregiverActionParams = {
  calendarDate?: string;
  caregiver: CaregiverProfileCaregiver;
  legacySectionLabel?: string;
  legacySubsectionLabel?: string;
  onDone?: () => void;
  selectedVisits?: EntityProfileCalendarVisit[];
  data?: unknown;
};

function openAction(action: CaregiverProfileAction, params: CaregiverActionParams) {
  dispatchCaregiverProfileAction({ action, ...params });
}

export type CaregiverProfileBridgeActions = {
  addDocumentUpload: (params: CaregiverActionParams & { data: unknown }) => void;
  approveCalendarClockLocation: (params: CaregiverActionParams) => void;
  downloadDocumentUpload: (params: CaregiverActionParams & { data: unknown }) => void;
  exportProfile: (params: CaregiverActionParams) => void;
  navigateToCaregiver: (caregiverId: CaregiverId) => void;
  navigateToPatient: (patientId: PatientId) => void;
  navigateToVisitBroadcast: (visitBroadcastId: number) => void;
  navigateToVisitInstance: (visitInstanceId: number) => void;
  openAbsence: (params: CaregiverActionParams) => void;
  openCalendarNote: (params: CaregiverActionParams) => void;
  openChangeStatus: (params: CaregiverActionParams) => void;
  openChat: (params: CaregiverActionParams) => void;
  openDialer: (params: { predefinedPhoneNumber: string }) => void;
  openExclusionLists: (params: CaregiverActionParams) => void;
  openExportProfileSettings: (params: CaregiverActionParams) => void;
  openLegacyCaregiverSection: (
    params: CaregiverActionParams & {
      legacySectionLabel: string;
      legacySubsectionLabel: string;
    }
  ) => void;
  openCaregiverPayRate: (params: CaregiverActionParams & { data?: unknown }) => void;
  openCaregiverPayRateReset: (params: CaregiverActionParams) => void;
  openCaregiverHoursOverride: (params: CaregiverActionParams & { data?: unknown }) => void;
  openNotes: (params: CaregiverActionParams) => void;
  removeDocumentUpload: (params: CaregiverActionParams & { data: unknown }) => void;
  sendDocumentUploadToEmail: (params: CaregiverActionParams & { data: unknown }) => void;
  sendDocumentUploadToFax: (params: CaregiverActionParams & { data: unknown }) => void;
  openSmsSender: (params: {
    caregiverId: CaregiverId;
    phoneNumber: string;
    type: "Caregiver";
  }) => void;
  setCalendarPto: (params: CaregiverActionParams) => void;
  unblockLogin: (params: CaregiverActionParams) => void;
  viewDocumentUpload: (params: CaregiverActionParams & { data: unknown }) => void;
};

export const caregiverProfileBridgeActions: CaregiverProfileBridgeActions = {
  addDocumentUpload: (params) => openAction("addDocumentUpload", params),
  approveCalendarClockLocation: (params) => openAction("approveCalendarClockLocation", params),
  downloadDocumentUpload: (params) => openAction("downloadDocumentUpload", params),
  exportProfile: (params) => openAction("exportProfile", params),
  navigateToCaregiver: openCaregiverProfile,
  navigateToPatient: openPatientProfileFromCaregiverModal,
  navigateToVisitBroadcast: openVisitBroadcast,
  navigateToVisitInstance: openVisitInstance,
  openAbsence: (params) => openAction("openNewAbsence", params),
  openCalendarNote: (params) => openAction("openNewCalendarNote", params),
  openChangeStatus: (params) => openAction("openChangeStatus", params),
  openChat: (params) => openAction("openChat", params),
  openDialer: (params) => {
    reactBridge.dispatch("CALL_CENTER:OPEN_DIALER_POPUP", params);
  },
  openExclusionLists: (params) => openAction("openExclusionLists", params),
  openExportProfileSettings: (params) => openAction("openExportProfileSettings", params),
  openLegacyCaregiverSection: (params) => openAction("openLegacyCaregiverSection", params),
  openCaregiverPayRate: (params) => openAction("openCaregiverPayRate", params),
  openCaregiverPayRateReset: (params) => openAction("openCaregiverPayRateReset", params),
  openCaregiverHoursOverride: (params) => openAction("openCaregiverHoursOverride", params),
  openNotes: (params) => openAction("openNotes", params),
  removeDocumentUpload: (params) => openAction("removeDocumentUpload", params),
  sendDocumentUploadToEmail: (params) => openAction("sendDocumentUploadToEmail", params),
  sendDocumentUploadToFax: (params) => openAction("sendDocumentUploadToFax", params),
  openSmsSender: (params) => {
    reactBridge.dispatch("OPEN_SMS_SENDER", params);
  },
  setCalendarPto: (params) => openAction("setCalendarPto", params),
  unblockLogin: (params) => openAction("unblockLogin", params),
  viewDocumentUpload: (params) => openAction("viewDocumentUpload", params),
};

function openCaregiverProfile(caregiverId: CaregiverId) {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: {
        type: "navigate",
        payload: {
          entity: "Caregiver",
          id: caregiverId,
        },
      },
    })
  );
}

function openPatientProfileFromCaregiverModal(patientId: PatientId) {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: {
        type: "navigate",
        payload: {
          entity: "Patient",
          id: patientId,
        },
        redirect: true,
      },
    })
  );
}

function openVisitBroadcast(visitBroadcastId: number) {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: {
        type: "navigate",
        payload: {
          entity: "VisitBroadcast",
          id: visitBroadcastId,
        },
      },
    })
  );
}

function openVisitInstance(visitInstanceId: number) {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: {
        type: "navigate",
        payload: {
          entity: "VisitInstance",
          id: visitInstanceId,
        },
      },
    })
  );
}
